/**
 * A creative's files by stage and round (Aziz 2026-09-29): Script, then the raws by
 * folder, then each rough cut round, each colouring round, each final edit round, then
 * the clinic's review, one under the other. Every round carries who submitted it, when,
 * and the decision with its note. The filters are the stages and, inside a stage, its
 * rounds, so a filter shows that stage's files only. The cards are the Assets cards.
 */
import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { CheckCircle2, CheckSquare, Clock, FileText, FolderOpen, RotateCcw, Hammer, Square } from 'lucide-react';
import { cn } from '@/lib/cn';
import { fetchStages, type MediaAsset, type StageRound, type StageSection } from '@/lib/queries';
import { bidi, BIDI_CLS } from '@/lib/bidi';
import RichView from '@/components/ui/RichView';

const STATE: Record<string, { label: string; cls: string; icon: JSX.Element }> = {
  approved:    { label: 'Approved',        cls: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30', icon: <CheckCircle2 size={12} /> },
  changes:     { label: 'Changes asked',   cls: 'bg-amber-500/15 text-amber-300 border-amber-500/30', icon: <RotateCcw size={12} /> },
  waiting:     { label: 'Waiting for a decision', cls: 'bg-sky-500/15 text-sky-300 border-sky-500/30', icon: <Clock size={12} /> },
  in_progress: { label: 'In progress',     cls: 'bg-slate-700/40 text-slate-300 border-slate-600/40', icon: <Hammer size={12} /> },
  replaced:    { label: 'Resubmitted',     cls: 'bg-slate-700/40 text-slate-400 border-slate-600/40', icon: <RotateCcw size={12} /> },
};

function when(d?: string | null) {
  return d ? new Date(d).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '';
}

function RoundHead({ s, r }: { s: StageSection; r: StageRound }) {
  const st = STATE[r.state] ?? STATE.in_progress;
  return (
    <div className="flex flex-wrap items-center gap-2 mb-2" data-testid="stage-round">
      <span className="text-sm font-medium text-muted-strong">{s.label} · Round {r.n}</span>
      <span className={cn('inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded border', st.cls)}>{st.icon}{st.label}</span>
      <span className="text-[11px] text-muted">
        {r.submitted_at && <>Submitted {r.submitted_by ? `by ${r.submitted_by} ` : ''}{when(r.submitted_at)}</>}
        {r.decided_at && <>{r.submitted_at ? ' · ' : ''}{r.state === 'approved' ? 'approved' : 'changes asked'} by {r.decided_by} {when(r.decided_at)}</>}
      </span>
      {r.carried_label
        ? <span className="text-[11px] text-muted" data-testid="stage-carried">Same files as {r.carried_label}</span>
        : r.carried_from && <span className="text-[11px] text-muted" data-testid="stage-carried">Same files as round {r.carried_from}</span>}
      {r.note && <div dir="auto" className={cn('basis-full text-xs text-amber-200/90 bg-amber-500/5 border border-amber-500/20 rounded-md px-2.5 py-1.5', BIDI_CLS)} data-testid="stage-note">{bidi(r.note)}</div>}
    </div>
  );
}

/** Tick a whole folder or round at once; ticking again takes them all out. */
function PickAll({ ids, selected, onSelect }: { ids: string[]; selected?: Set<string>; onSelect?: (ids: string[], on: boolean) => void }) {
  if (!onSelect || !selected || ids.length === 0) return null;
  const all = ids.every((i) => selected.has(i));
  return (
    <button type="button" onClick={() => onSelect(ids, !all)} data-testid="stage-select"
      className="inline-flex items-center gap-1 text-[11px] text-muted hover:text-muted-strong whitespace-nowrap">
      {all ? <CheckSquare size={12} className="text-accent-400" /> : <Square size={12} />} {all ? 'Unselect' : 'Select'} {ids.length}
    </button>
  );
}

export default function StageView({ creativeId, assets, renderCard, selected, onSelect }: {
  /** the Assets panel's selection, so the bulk bar works here too */
  selected?: Set<string>;
  onSelect?: (ids: string[], on: boolean) => void;
  creativeId: string;
  assets: MediaAsset[];
  /** the Assets panel's own card, with its siblings for the viewer's strip */
  renderCard: (a: MediaAsset, siblings: MediaAsset[]) => JSX.Element;
}) {
  const q = useQuery({ queryKey: ['stages', creativeId], queryFn: () => fetchStages(creativeId) });
  const [stage, setStage] = useState<string>('all');
  const [round, setRound] = useState<number | 'all'>('all');
  const byId = useMemo(() => new Map(assets.map((a) => [a.id, a])), [assets]);
  const pick = (ids: string[]) => ids.map((i) => byId.get(i)).filter(Boolean) as MediaAsset[];
  const sections = (q.data ?? []).filter((s) => s.count > 0 || s.rounds.length > 0 || s.text);
  const shown = stage === 'all' ? sections : sections.filter((s) => s.key === stage);
  const current = sections.find((s) => s.key === stage);
  const grid = (list: MediaAsset[]) => list.length
    ? <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">{list.map((a) => renderCard(a, list))}</div>
    : <div className="text-[11px] text-muted py-2">No files in this round.</div>;

  if (q.isLoading) return <div className="text-xs text-muted py-4 text-center">Loading stages...</div>;
  return (
    <div data-testid="stage-view">
      <div className="flex flex-wrap gap-1.5 mb-2" role="tablist" aria-label="Stages">
        {[{ key: 'all', label: 'All stages', n: sections.reduce((a, s) => a + (s.key === 'client' ? 0 : s.count), 0) }, ...sections.map((s) => ({ key: s.key, label: s.label, n: s.count }))].map((t) => (
          <button key={t.key} role="tab" aria-selected={stage === t.key} onClick={() => { setStage(t.key); setRound('all'); }} data-testid={`stage-tab-${t.key}`}
            className={cn('text-xs px-3 py-1.5 rounded-full border', stage === t.key ? 'border-accent-400 text-accent-400 bg-accent-600/10' : 'border-slate-800 text-muted hover:text-muted-strong')}>
            {t.label} <span className="tabular-nums">{t.n}</span>
          </button>
        ))}
      </div>
      {current && current.rounds.some((r) => r.asset_ids.length > 0) && current.rounds.length > 1 && (
        <div className="flex flex-wrap gap-1.5 mb-3" role="tablist" aria-label="Rounds">
          {(['all', ...current.rounds.map((r) => r.n)] as (number | 'all')[]).map((n) => {
            const r = current.rounds.find((x) => x.n === n);
            return (
              <button key={String(n)} role="tab" aria-selected={round === n} onClick={() => setRound(n)} data-testid={`round-tab-${n}`}
                className={cn('text-[11px] px-2.5 py-1 rounded-full border', round === n ? 'border-accent-400 text-accent-400 bg-accent-600/10' : 'border-slate-800 text-muted hover:text-muted-strong')}>
                {n === 'all' ? 'All rounds' : `Round ${n}`} <span className="tabular-nums">{n === 'all' ? current.count : r?.asset_ids.length}</span>
              </button>
            );
          })}
        </div>
      )}

      <div className="space-y-6">
        {shown.map((s) => (
          <section key={s.key} data-testid={`stage-${s.key}`} className="border-l-2 border-slate-800 pl-4">
            <h4 className="text-[11px] uppercase tracking-wide text-muted mb-2">{s.label}</h4>
            {s.key === 'script' && s.text && (
              <div className="text-sm text-muted-strong bg-bg-card/40 border border-slate-800 rounded-lg p-3 mb-3 max-h-64 overflow-auto" data-testid="stage-script">
                <FileText size={13} className="inline mr-1.5 text-muted" />
                <RichView html={s.html} plain={s.text} />
              </div>
            )}
            {s.groups.map((g) => (
              <div key={g.name} className="mb-4">
                <div className="flex items-center gap-1.5 text-xs text-muted mb-2" dir="auto"><FolderOpen size={12} /> {g.name} <span className="text-muted-strong">{g.asset_ids.length}</span>
                  <span className="ml-auto"><PickAll ids={pick(g.asset_ids).map((a) => a.id)} selected={selected} onSelect={onSelect} /></span></div>
                {grid(pick(g.asset_ids))}
              </div>
            ))}
            {s.rounds.filter((r) => round === 'all' || s.key !== stage || r.n === round).map((r) => (
              <div key={r.n} className="mb-4">
                <div className="flex items-start gap-2"><div className="flex-1 min-w-0"><RoundHead s={s} r={r} /></div>
                  {(s.key !== 'raws' && s.key !== 'script') && <PickAll ids={pick(r.asset_ids).map((a) => a.id)} selected={selected} onSelect={onSelect} />}</div>
                {(s.key !== 'raws' && s.key !== 'script') && grid(pick(r.asset_ids))}
              </div>
            ))}
            {s.rounds.length === 0 && s.groups.length === 0 && !s.text && <div className="text-[11px] text-muted">Nothing here yet.</div>}
          </section>
        ))}
        {shown.length === 0 && <div className="text-xs text-muted py-6 text-center">No files yet.</div>}
      </div>
    </div>
  );
}
